/**
 * Дашборд «Спецоценка» (`/sout`, canon service-sout). Светофор-счётчики (Просрочено/Истекает скоро/
 * Действует/Не проводилась), таблица рабочих мест (Рабочее место(должность)/Класс УТ/СОУТ проведена/
 * действует до/статус) с группировкой по подразделениям и «бессрочно» для класса 1–2, фильтры
 * (подразделение + поиск), кнопка «Заказать» (на overdue/soon/none) и блок «Заказы по спецоценке».
 */
import { useEffect, useMemo, useState } from 'react'
import { ShieldCheck } from 'lucide-react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  ordersApi,
  soutApi,
  type OrderCard,
  type SoutBulkRejection,
  type SoutDashboard,
  type SoutDashboardRow,
} from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { backState } from '@/lib/use-back-target'
import { useWorkplaceSelection } from '@/lib/use-workplace-selection'
import { formatShortName } from '@/lib/format-name'
import {
  SOUT_COUNTER_LABELS,
  SOUT_STATUS_VIEW,
  canOrderSout,
  validUntilText,
  workplacesCountLabel,
} from '@/lib/sout'
import { clientBadge, compositionSummary, STAGES_BY_SERVICE } from '@/lib/orders'
import { PAGE_SIZE, pageCount } from '@/lib/pagination'
import { AppLink } from '@/app/navigation'
import { Pagination } from '@/design-system/components/data/pagination'
import { PageHead } from '@/design-system/components/layout/page-head'
import { Panel } from '@/design-system/components/layout/panel'
import { Card } from '@/design-system/components/layout/card'
import { Toolbar } from '@/design-system/components/layout/toolbar'
import { Button } from '@/design-system/components/primitives/button'
import { Badge } from '@/design-system/components/primitives/badge'
import { Input } from '@/design-system/components/primitives/input'
import { Checkbox } from '@/design-system/components/primitives/checkbox'
import { Alert } from '@/design-system/components/feedback/alert'
import { EmptyState } from '@/design-system/components/feedback/empty-state'
import { StatCard } from '@/design-system/components/data/stat-card'
import { StatGrid } from '@/design-system/components/data/stat-grid'
import { ListItem } from '@/design-system/components/data/list-item'
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableGroupRow,
  DataTableHead,
  DataTableHeaderCell,
  DataTableRow,
} from '@/design-system/components/data/data-table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/design-system/components/primitives/select'

const ALL = 'all'
const SOUT_STAGE_COUNT = STAGES_BY_SERVICE.sout.length

/**
 * Ключ персистентности мультивыбора (§8, tasks.md 4.3a, решение оркестратора). Сама механика
 * хранения/восстановления/очистки — общая с «Профрисками» (`useWorkplaceSelection`,
 * centrika-docs!35), здесь остаётся только ключ страницы.
 */
const SELECTION_STORAGE_KEY = 'centrika:sout-dashboard:selected-workplaces'

/**
 * Человекочитаемое сообщение о частично/полностью неудачном bulk-добавлении (§8, D3 доп.
 * решение волны 4 — см. `design.md`): заказ УЖЕ создан к моменту вызова, поэтому мы не блокируем
 * пользователя — переходим в мастер и показываем это сообщение там (`location.state.
 * bulkAddWarning`, читает `OrderSoutWizardPage`), а не остаёмся на «Спецоценке».
 */
function bulkRejectionMessage(rejected: SoutBulkRejection[], rows: SoutDashboardRow[]): string {
  const label = (r: SoutBulkRejection): string => {
    if (r.personId) {
      for (const row of rows) {
        const emp = row.employees.find((e) => e.id === r.personId)
        if (emp) return `${emp.fullName}: ${r.reason}`
      }
      return r.reason
    }
    return r.reason
  }
  return `Часть выбранных рабочих мест не удалось добавить в заказ: ${rejected.map(label).join('; ')}.`
}

function formatDate(iso?: string): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function validUntilLabel(row: SoutDashboardRow): string {
  const t = validUntilText(row)
  if (t.kind === 'perpetual') return 'бессрочно'
  if (t.kind === 'none') return 'не проводилась'
  return formatDate(t.date)
}

function Counters({ counters }: { counters: SoutDashboard['counters'] }) {
  return (
    <StatGrid className="mb-5">
      <StatCard label={SOUT_COUNTER_LABELS.overdue} value={counters.overdue} status="danger" />
      <StatCard label={SOUT_COUNTER_LABELS.soon} value={counters.soon} status="warning" />
      <StatCard label={SOUT_COUNTER_LABELS.valid} value={counters.valid} status="success" />
      <StatCard label={SOUT_COUNTER_LABELS.none} value={counters.none} noData />
    </StatGrid>
  )
}

function PlacesTable({
  rows,
  onOrder,
  ordering,
  from,
  selected,
  onToggleSelected,
}: {
  rows: SoutDashboardRow[]
  onOrder: () => void
  ordering: boolean
  /** D1, p3-structure-sout-training §2: адрес этой страницы (`backState`) — передаётся в ссылку
   * на карточку места как `state.from`, чтобы «Назад» вернул сюда, а не на фолбэк «Охрана труда». */
  from: string
  /** §8: текущий мультивыбор мест (набор `workplaceId`) для нового заказа СОУТ. */
  selected: Set<string>
  onToggleSelected: (workplaceId: string) => void
}) {
  // Группировка по подразделению с сохранением порядка появления.
  const groups: Array<{ department: string; items: SoutDashboardRow[] }> = []
  const idx = new Map<string, number>()
  for (const r of rows) {
    const dep = r.departmentName || 'Без подразделения'
    if (!idx.has(dep)) {
      idx.set(dep, groups.length)
      groups.push({ department: dep, items: [] })
    }
    groups[idx.get(dep)!].items.push(r)
  }

  return (
    <Panel>
      <DataTable>
        <DataTableHead>
          <tr>
            <DataTableHeaderCell className="w-10">
              <span className="sr-only">Выбор</span>
            </DataTableHeaderCell>
            <DataTableHeaderCell>Рабочее место</DataTableHeaderCell>
            <DataTableHeaderCell>Класс УТ</DataTableHeaderCell>
            <DataTableHeaderCell>СОУТ проведена</DataTableHeaderCell>
            <DataTableHeaderCell>Действует до</DataTableHeaderCell>
            <DataTableHeaderCell>Статус</DataTableHeaderCell>
            <DataTableHeaderCell>{''}</DataTableHeaderCell>
          </tr>
        </DataTableHead>
        <DataTableBody>
          {groups.map((g) => (
            <GroupRows
              key={g.department}
              group={g}
              onOrder={onOrder}
              ordering={ordering}
              from={from}
              selected={selected}
              onToggleSelected={onToggleSelected}
            />
          ))}
        </DataTableBody>
      </DataTable>
    </Panel>
  )
}

function GroupRows({
  group,
  onOrder,
  ordering,
  from,
  selected,
  onToggleSelected,
}: {
  group: { department: string; items: SoutDashboardRow[] }
  onOrder: () => void
  ordering: boolean
  from: string
  selected: Set<string>
  onToggleSelected: (workplaceId: string) => void
}) {
  return (
    <>
      <DataTableGroupRow colSpan={7}>{group.department}</DataTableGroupRow>
      {group.items.map((r) => {
        const view = SOUT_STATUS_VIEW[r.status]
        // §8: мультивыбор доступен только там, где сейчас доступно и одиночное «Заказать»
        // (место со статусом «Действует» уже не нуждается в новом заказе СОУТ).
        // p3-critical-fixes (№3а): дополнительно — только у мест, связанных хотя бы с одним
        // сотрудником; у вакантного места чекбокс не показывается (в заказ СОУТ строки без
        // сотрудника не попадают). Пометка «Рабочее место вакантно» ниже сохраняется (§4).
        const selectable = canOrderSout(r.status) && r.employees.length > 0
        return (
          <DataTableRow key={r.workplaceId}>
            <DataTableCell>
              {selectable ? (
                <Checkbox
                  checked={selected.has(r.workplaceId)}
                  onCheckedChange={() => onToggleSelected(r.workplaceId)}
                  aria-label={`Выбрать рабочее место «${r.position}»`}
                />
              ) : null}
            </DataTableCell>
            <DataTableCell className="font-medium text-txt">
              {/* F2-13: переход на карточку рабочего места (§9.5) — строка результата раньше
                  никуда не вела; ссылка — только на названии места, не на всю строку.
                  D1, p3-structure-sout-training §2: state.from = адрес «Спецоценки» (code-review
                  волны 3, MEDIUM: через backState/useLocation, не статический литерал — переживёт
                  будущие query-параметры волны 4, напр. мультивыбор) — «Назад» в карточке места
                  должен вернуть сюда, а не на фолбэк «Охрана труда». */}
              <AppLink to={`/workplaces/${r.workplaceId}`} state={{ from }}>
                {r.position}
              </AppLink>
              {/* p3-structure-sout-training §4/D7: связанные сотрудники места (включая
                  сменщиков) — мелким серым текстом, каждое ФИО ссылка на карточку сотрудника,
                  тот же state.from, чтобы «Назад» тоже вернул на «Спецоценку». */}
              {r.employees.length === 0 ? (
                <p className="mt-0.5 text-12 font-normal text-txt-secondary">Рабочее место вакантно</p>
              ) : (
                <p className="mt-0.5 text-12 font-normal text-txt-secondary">
                  {r.employees.map((e, i) => (
                    <span key={e.id}>
                      <AppLink to={`/staff/${e.id}`} state={{ from }}>
                        {formatShortName(e.fullName)}
                      </AppLink>
                      {i < r.employees.length - 1 ? ', ' : ''}
                    </span>
                  ))}
                </p>
              )}
            </DataTableCell>
            <DataTableCell>{r.hazardClass || '—'}</DataTableCell>
            <DataTableCell>{r.conductedOn ? formatDate(r.conductedOn) : 'не проводилась'}</DataTableCell>
            <DataTableCell>{validUntilLabel(r)}</DataTableCell>
            <DataTableCell>
              <Badge status={view.role}>{view.label}</Badge>
            </DataTableCell>
            <DataTableCell className="text-right">
              {/* p3-structure-sout-training §3: точка входа исторического ввода СОУТ убрана со
                  «Спецоценки» — теперь только из карточки рабочего места/сотрудника. */}
              {canOrderSout(r.status) ? (
                <Button size="sm" disabled={ordering} onClick={onOrder}>
                  Заказать
                </Button>
              ) : null}
            </DataTableCell>
          </DataTableRow>
        )
      })}
    </>
  )
}

function SoutOrders() {
  const navigate = useNavigate()
  const [items, setItems] = useState<OrderCard[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const controller = new AbortController()
    ordersApi
      .list(
        { service: 'sout', limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE },
        controller.signal,
      )
      .then((res) => {
        if (controller.signal.aborted) return
        // Страница «уехала» за пределы результатов — возвращаемся на первую.
        if (res.items.length === 0 && page > 1) {
          setPage(1)
          return
        }
        setItems(res.items)
        setTotal(res.total)
      })
      .catch(() => undefined)
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [page])

  return (
    <Card header="Заказы по спецоценке" className="mt-6">
      {loading ? (
        <p className="py-2 text-txt-secondary">Загрузка…</p>
      ) : items.length === 0 ? (
        <p className="py-2 text-txt-secondary">Заказов по спецоценке пока нет.</p>
      ) : (
        items.map((o) => {
          const badge = clientBadge(o.dataStatus)
          const isDraft = o.dataStatus === 'draft'
          return (
            <ListItem
              key={o.id}
              name={o.itemCount > 0 ? compositionSummary(o.service, o.itemCount) : 'Заказ СОУТ'}
              meta={`Этап: ${o.currentStage.label} (${o.currentStage.position + 1} из ${SOUT_STAGE_COUNT})`}
              action={
                <div className="flex items-center gap-2.5">
                  <Badge status={badge.role}>{badge.label}</Badge>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => navigate(isDraft ? `/orders/${o.id}/sout` : `/orders/${o.id}`)}
                  >
                    {isDraft ? 'Продолжить' : 'Открыть'}
                  </Button>
                </div>
              }
            />
          )
        })
      )}
      <Pagination page={page} totalPages={pageCount(total)} onChange={setPage} />
    </Card>
  )
}

export function SoutDashboardPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [data, setData] = useState<SoutDashboard | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [department, setDepartment] = useState(ALL)
  const [search, setSearch] = useState('')
  const [ordering, setOrdering] = useState(false)
  const [orderError, setOrderError] = useState<string | null>(null)
  // §8, tasks.md 4.3a: мультивыбор мест для нового заказа — восстанавливается из sessionStorage
  // при монтировании (переживает переход в карточку места по ссылке §4 и возврат «Назад»).
  const {
    selected,
    toggle: toggleSelected,
    clear: clearSelection,
    reconcile: reconcileSelection,
  } = useWorkplaceSelection(SELECTION_STORAGE_KEY)

  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    setError(null)
    soutApi
      .dashboard(controller.signal)
      .then((res) => {
        if (!controller.signal.aborted) setData(res)
      })
      .catch((err) => {
        if (!controller.signal.aborted) setError(getErrorMessage(err, 'Не удалось загрузить дашборд'))
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [])

  // Восстановленный из sessionStorage выбор сверяем со свежими данными дашборда: место могло
  // перестать быть «заказуемым» (стало valid), потерять всех сотрудников (стало вакантным —
  // p3-critical-fixes №3а) или пропасть — такие id тихо отбрасываем, чтобы счётчик и
  // bulk-запрос не тянули устаревшие/невыбираемые workplaceId (тот же гейт, что и у чекбокса).
  useEffect(() => {
    if (!data) return
    reconcileSelection(
      new Set(
        data.rows
          .filter((r) => canOrderSout(r.status) && r.employees.length > 0)
          .map((r) => r.workplaceId),
      ),
    )
  }, [data, reconcileSelection])

  const departments = useMemo(() => {
    const set = new Set<string>()
    for (const r of data?.rows ?? []) set.add(r.departmentName || 'Без подразделения')
    return Array.from(set)
  }, [data])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return (data?.rows ?? []).filter((r) => {
      const dep = r.departmentName || 'Без подразделения'
      if (department !== ALL && dep !== department) return false
      if (q && !`${r.position} ${dep}`.toLowerCase().includes(q)) return false
      return true
    })
  }, [data, department, search])

  /** Обычное создание пустого заказа (кнопки «Заказать» в строках места; шапка — без выбора). */
  async function createBlankOrder() {
    setOrderError(null)
    setOrdering(true)
    try {
      const order = await ordersApi.create({ service: 'sout' })
      navigate(`/orders/${order.id}/sout`)
    } catch (err) {
      setOrderError(getErrorMessage(err, 'Не удалось создать заказ СОУТ'))
      setOrdering(false)
    }
  }

  /**
   * §8: создание НОВОГО заказа (решение заказчика — design D3) с выбранными местами, добавленными
   * ОДНИМ вызовом `from-persons`: занятые места → `personId` всех связанных сотрудников (включая
   * сменщиков — уже в `row.employees` после джойна §4). p3-critical-fixes (№3а): вакантные места
   * в мультивыборе отсутствуют (у них нет чекбокса), поэтому `workplaceId` вакансий не собирается
   * и не отправляется — в заказ идут только связанные сотрудники.
   *
   * №1 (p3-critical-fixes): после создания заказа мастер открывается с ПЕРВОГО шага «Данные о
   * компании» (без `?step=`), заполнение — последовательно по шагам; выбранные места уже
   * добавлены в заказ (bulk до навигации) и видны при доходе до шага «Рабочие места».
   *
   * Обработка неполного успеха (design.md, доп. решение волны 4): к моменту вызова `from-persons`
   * заказ УЖЕ создан и часть/все места уже могли добавиться — останавливать пользователя на
   * «Спецоценке» и заставлять решать, что делать с наполовину заведённым заказом, хуже, чем
   * пустить его в уже открытый мастер с предупреждением (там уже есть «Из структуры компании»
   * для ручного добора). Поэтому и `rejected[]`, и полный сбой самого bulk-вызова (сеть/5xx)
   * ОДИНАКОВО не блокируют переход — предупреждение передаётся через `location.state.
   * bulkAddWarning` и показывается уже в `OrderSoutWizardPage`.
   */
  async function createOrderWithSelection() {
    setOrderError(null)
    setOrdering(true)
    const rows = data?.rows ?? []
    try {
      const order = await ordersApi.create({ service: 'sout' })
      // Заказ создан — выбор своё дело сделал, чистим его независимо от исхода bulk-добавления.
      clearSelection()

      // Только связанные сотрудники выбранных (занятых) мест — вакантные места невыбираемы (№3а).
      const personIds: string[] = []
      for (const row of rows) {
        if (!selected.has(row.workplaceId)) continue
        for (const e of row.employees) personIds.push(e.id)
      }

      try {
        const result = await soutApi.bulkAddFromStructure(order.id, { personIds })
        if (result.rejected.length > 0) {
          navigate(`/orders/${order.id}/sout`, {
            state: { bulkAddWarning: bulkRejectionMessage(result.rejected, rows) },
          })
          return
        }
        navigate(`/orders/${order.id}/sout`)
      } catch (bulkErr) {
        navigate(`/orders/${order.id}/sout`, {
          state: {
            bulkAddWarning: getErrorMessage(
              bulkErr,
              'Не удалось автоматически добавить выбранные рабочие места в заказ. Добавьте их вручную через «Из структуры компании».',
            ),
          },
        })
      }
    } catch (err) {
      setOrderError(getErrorMessage(err, 'Не удалось создать заказ СОУТ'))
      setOrdering(false)
    }
  }

  function handleHeaderOrderClick() {
    void (selected.size > 0 ? createOrderWithSelection() : createBlankOrder())
  }

  return (
    <div>
      <PageHead
        title="Спецоценка"
        subtitle="Состояние СОУТ по рабочим местам вашей организации"
        actions={
          <div className="flex items-center gap-3">
            {selected.size > 0 ? (
              <span className="text-txt-secondary">Выбрано: {workplacesCountLabel(selected.size)}</span>
            ) : null}
            <Button onClick={handleHeaderOrderClick} disabled={ordering}>
              Заказать СОУТ
            </Button>
          </div>
        }
      />

      {orderError ? (
        <Alert variant="danger" className="mb-4">
          {orderError}
        </Alert>
      ) : null}

      {error ? (
        <Alert variant="danger">{error}</Alert>
      ) : loading || !data ? (
        <p className="py-10 text-center text-txt-secondary">Загрузка…</p>
      ) : (
        <>
          <Counters counters={data.counters} />

          <Toolbar>
            <Select value={department} onValueChange={setDepartment}>
              <SelectTrigger aria-label="Фильтр по подразделению" className="w-auto">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Все подразделения</SelectItem>
                {departments.map((d) => (
                  <SelectItem key={d} value={d}>
                    {d}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Поиск по должности или подразделению"
              className="max-w-narrow"
              aria-label="Поиск рабочих мест"
            />
          </Toolbar>

          {filtered.length === 0 ? (
            <Panel>
              <EmptyState
                icon={ShieldCheck}
                title="Рабочих мест не найдено"
                description="Измените фильтр или добавьте рабочие места в структуре компании"
              />
            </Panel>
          ) : (
            <PlacesTable
              rows={filtered}
              onOrder={() => void createBlankOrder()}
              ordering={ordering}
              from={backState(location).from}
              selected={selected}
              onToggleSelected={toggleSelected}
            />
          )}

          <SoutOrders />
        </>
      )}
    </div>
  )
}
